import React, { useCallback, useMemo } from 'react';

import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import LoyaltyProgramRow from './LoyaltyProgramRow';
import { LoyaltyProgramConfig, LoyaltyProgramRow as LoyaltyProgramRowType } from './loyaltyProgramsConfig';

interface Props {
  title: string;
  programType: 'flight' | 'hotel';
  programConfigMap: Record<string, LoyaltyProgramConfig>;
  rows: Array<LoyaltyProgramRowType>;
  onRowsChange: (rows: Array<LoyaltyProgramRowType>) => void;
  isSubmitting: boolean;
}

export default function LoyaltyProgramSection({
  title,
  programType,
  programConfigMap,
  rows,
  onRowsChange,
  isSubmitting,
}: Props) {
  // Get available programs for a specific row (excluding already selected programs)
  const getAvailablePrograms = useCallback(
    (currentRowId: string) => {
      const selectedPrograms = rows.filter((row) => row.id !== currentRowId && row.program).map((row) => row.program);

      return Object.keys(programConfigMap).filter((program) => !selectedPrograms.includes(program));
    },
    [rows, programConfigMap],
  );

  // Check if we can add more rows
  const canAddMore = useMemo(() => {
    const selectedCount = rows.length;
    const totalPrograms = Object.keys(programConfigMap).length;
    return selectedCount < totalPrograms;
  }, [rows, programConfigMap]);

  // Update a specific row
  const updateRow = useCallback(
    (id: string, field: 'program' | 'memberNumber', value: string) => {
      const updatedRows = rows.map((row) => (row.id === id ? { ...row, [field]: value } : row));
      onRowsChange(updatedRows);
    },
    [rows, onRowsChange],
  );

  // Add a new empty row
  const addRow = useCallback(() => {
    if (!canAddMore) {
      return;
    }

    const newRow: LoyaltyProgramRowType = {
      id: crypto.randomUUID(),
      program: '',
      memberNumber: '',
      type: programType,
    };

    onRowsChange([...rows, newRow]);
  }, [canAddMore, programType, rows, onRowsChange]);

  const addButtonText = `+ Add ${programType === 'flight' ? 'Frequent Flyer' : 'Hotel Loyalty'} Program`;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {title}
      </Typography>

      <Stack spacing={3}>
        {rows.map((row, index) => {
          const availablePrograms = getAvailablePrograms(row.id);
          const isLastRow = index === rows.length - 1;

          return (
            <Box key={row.id}>
              <LoyaltyProgramRow
                row={row}
                programConfigMap={programConfigMap}
                availablePrograms={availablePrograms}
                onUpdate={(field, value) => updateRow(row.id, field, value)}
                isSubmitting={isSubmitting}
              />
              {!isLastRow && (
                <Box sx={{ mt: 3, display: { xs: 'block', md: 'none' } }}>
                  <Divider />
                </Box>
              )}
            </Box>
          );
        })}

        {canAddMore && (
          <Box>
            <Button variant="text" onClick={addRow} type="button" disabled={isSubmitting}>
              {addButtonText}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
